import { useEffect, useRef } from 'react';
import maplibregl from 'maplibre-gl';
import { MAP_MIN_LOT_ZOOM, HOVER_LOT_STYLE } from '../styles/mapTheme';
import { buildParcelHoverTooltipHtml } from '../utils/parcelHoverTooltip';
import { getFeatureLotId } from './useLotSelection';
import { isMapAlive } from '../utils/canAccessMapStyle';

/** MapLibre expone `sourceLayer` en el layer runtime; el spec usa `source-layer`. */
function getLayerSourceLayer(layer) {
  if (!layer) return null;
  return layer.sourceLayer || layer['source-layer'] || null;
}

/**
 * Vector sources (PMTiles) rechazan setFeatureState sin sourceLayer y lo
 * loguean en consola sin tirar — el try/catch no alcanza.
 * Mapa destruido (`remove()` → `map.style` undefined, p. ej. al desmontar MapPage o en
 * hot reload): `getSource` tira; acá devuelve false.
 */
export function canSetFeatureState(map, featureRef) {
  if (!featureRef?.source || !isMapAlive(map)) return false;
  let source;
  try {
    source = map.getSource(featureRef.source);
  } catch {
    return false;
  }
  if (!source) return false;
  if (source.type === 'vector' && !featureRef.sourceLayer) return false;
  return true;
}

/**
 * Tooltip + hover de parcelas PMTiles
 * ------------------------------------
 * Dónde se renderiza:
 * - Hook `useParcelHover` (este archivo) crea un `maplibregl.Popup` con
 *   className `terrio-parcel-tooltip`; el HTML lo arma
 *   `utils/parcelHoverTooltip.js`.
 * - Estilos: `src/Map/Map.css` (`.terrio-parcel-tooltip*`).
 * - Quién lo enciende:
 *   - `MapPage` pasa `showTooltip` en Listas → Agregar lotes → tab "Desde el
 *     mapa", y también sobre polígonos pintados: filtros, alcance (registros
 *     / lista) o resaltado verde del panel. El borde de hover sigue en todas.
 *   - `SharedListPage` lo enciende con pines (rollback). Con chips de precio
 *     queda apagado: la publicación se lee en el tooltip del chip y el cartel
 *     del lote se pisaba con los precios. El borde y el cursor siguen.
 *
 * Props típicas del feature en tiles de parcelas (PMTiles):
 * - `id`            — id del lote (también puede venir como feature.id)
 * - `name`          — número / nomenclatura catastral (tooltip)
 * - `state`         — estado / jurisdicción del tile
 * - `type`          — tipo de parcela en el tile
 * - `surface`       — superficie total (si el tile la expone)
 * - `urbansurface`  — superficie urbana m² (tooltip)
 *
 * Resolución de lot id en clicks: `getFeatureLotId` en `useLotSelection.js`
 * (feature.id o props id / lot_id / etc.).
 */

/**
 * Resalta parcelas PMTiles al pasar el cursor usando feature-state de MapLibre.
 *
 * En lugar de copiar geometría a un GeoJSON separado (que arrastra artefactos
 * de clipping por tile), toggleamos el estado `hover` sobre el feature original.
 * Las capas de hover (agregadas por usePmtilesLayers) reaccionan al feature-state
 * para mostrar/ocultar el borde sin artefactos.
 */
export function useParcelHover({
  mapInstanceRef,
  getInteractiveParcelLayerIds,
  enabled = true,
  /** Tooltip con catastral / superficie: solo en flujos que lo pidan (p. ej. agregar desde mapa). */
  showTooltip = false,
  /**
   * Limita el hover a un subconjunto de parcelas. En listas compartidas solo se
   * puede abrir el detalle de los lotes de la lista: sin esto, el cursor y el
   * borde prometerían un click que no hace nada en las parcelas ajenas.
   */
  canHoverFeature = null,
  /**
   * Si el tooltip aplica a esta feature. El borde y el cursor no se tocan:
   * en el mapa principal el click sigue abriendo cualquier parcela.
   */
  canShowTooltip = null,
  /** HTML del tooltip. Recibe `(properties, feature)`; por defecto, datos del tile.
   *  También `{ html, variant: 'share' | 'parcel' }` para cambiar el chrome en ese hover. */
  buildTooltipHtml = buildParcelHoverTooltipHtml,
  /** Hover desde el panel de lista (id de lote). No abre tooltip por sí solo. */
  externalHoverLotId = null,
  /**
   * Tooltip de publicación abierto desde la card del detalle de lista.
   * `{ lngLat: [lng, lat], html }`.
   */
  externalHoverPopup = null,
  /** Lote bajo el cursor del mapa, o null. Solo cuando cambia. */
  onHoverLotId = null,
  /**
   * Capas que tapan el hover de parcela (pines de lugares en el share).
   * Si hay un hit, no se pinta borde ni tooltip de lote.
   */
  occludingLayerIds = null,
  /**
   * Chrome del popup: `parcel` = burbuja chica 54px (mapa / agregar lotes);
   * `share` = card apilada de CAD `/#/lista/:id` (`terrio-cad-share-tooltip`).
   */
  tooltipVariant = 'parcel',
}) {
  const hoveredRef      = useRef(null);
  const cachedLayersRef = useRef([]);
  const rafIdRef        = useRef(null);
  const popupRef        = useRef(null);
  const enabledRef      = useRef(enabled);
  const showTooltipRef  = useRef(showTooltip);
  const canHoverRef     = useRef(canHoverFeature);
  const canShowTooltipRef = useRef(canShowTooltip);
  const buildTooltipRef = useRef(buildTooltipHtml);
  const occludingLayerIdsRef = useRef(occludingLayerIds);
  const tooltipVariantRef = useRef(tooltipVariant);
  const onHoverLotIdRef = useRef(onHoverLotId);
  const hoverLotNotifyRef = useRef(null);
  enabledRef.current    = enabled;
  showTooltipRef.current = showTooltip;
  canHoverRef.current   = canHoverFeature;
  canShowTooltipRef.current = canShowTooltip;
  buildTooltipRef.current = buildTooltipHtml;
  occludingLayerIdsRef.current = occludingLayerIds;
  tooltipVariantRef.current = tooltipVariant;
  onHoverLotIdRef.current = onHoverLotId;

  // Al togglear parcelas, invalidamos la caché de capas y limpiamos hover residual.
  useEffect(() => {
    const map = mapInstanceRef.current;
    if (!map) return;

    if (!enabled) {
      if (hoveredRef.current) {
        try {
          if (canSetFeatureState(map, hoveredRef.current)) {
            map.setFeatureState(hoveredRef.current, { hover: false });
          }
        } catch {
          // ignore
        }
        hoveredRef.current = null;
        map.getCanvas().style.cursor = '';
      }
      if (popupRef.current) {
        popupRef.current.remove();
        popupRef.current = null;
      }
    } else {
      cachedLayersRef.current = [];
    }
  }, [enabled, mapInstanceRef]);

  // Si se apaga el tooltip (salió del modo agregar-desde-mapa), ocultarlo sin tocar el borde.
  useEffect(() => {
    if (showTooltip) return;
    if (popupRef.current) {
      popupRef.current.remove();
      popupRef.current = null;
    }
  }, [showTooltip]);

  useEffect(() => {
    const map = mapInstanceRef.current;
    if (!map || !getInteractiveParcelLayerIds) return;

    const canvas = map.getCanvas();

    const safeSetFeatureHoverState = (featureRef, hover) => {
      if (!canSetFeatureState(map, featureRef)) return false;
      try {
        map.setFeatureState(featureRef, { hover });
        return true;
      } catch {
        return false;
      }
    };

    const isShareTooltip = () => tooltipVariantRef.current === 'share';

    const notifyHoverLot = (feature) => {
      const raw = feature ? getFeatureLotId(feature) : null;
      const key = raw != null && raw !== '' ? String(raw) : null;
      if (hoverLotNotifyRef.current === key) return;
      hoverLotNotifyRef.current = key;
      onHoverLotIdRef.current?.(key);
    };

    const resolveTooltip = (feature) => {
      const wantsTooltip = showTooltipRef.current
        && (!canShowTooltipRef.current || canShowTooltipRef.current(feature));
      if (!wantsTooltip) return { html: null, share: isShareTooltip() };
      const built = buildTooltipRef.current(feature.properties || {}, feature);
      if (built && typeof built === 'object') {
        return { html: built.html || null, share: built.variant === 'share' };
      }
      return { html: built || null, share: isShareTooltip() };
    };

    const createHoverPopup = (share) => new maplibregl.Popup({
      closeButton: false,
      closeOnClick: false,
      focusAfterOpen: false,
      offset: 14,
      className: share
        ? 'terrio-parcel-tooltip terrio-cad-share-tooltip'
        : 'terrio-parcel-tooltip',
      maxWidth: share ? '240px' : '259px',
    });

    const isNodeInPopup = (node) => {
      const el = popupRef.current?.getElement();
      return Boolean(el && node instanceof Node && el.contains(node));
    };

    const bindShareTooltipBridge = (popup) => {
      if (!isShareTooltip()) return;
      const el = popup.getElement();
      if (!el || el.dataset.sharedBridge === '1') return;
      el.dataset.sharedBridge = '1';
      el.addEventListener('mouseleave', (event) => {
        if (event.relatedTarget instanceof Node && canvas.contains(event.relatedTarget)) return;
        clearHover();
      });
    };

    const ensurePopup = (share) => {
      if (popupRef.current && popupRef.current._sharedShare !== share) {
        popupRef.current.remove();
        popupRef.current = null;
      }
      if (popupRef.current) return popupRef.current;
      popupRef.current = createHoverPopup(share);
      popupRef.current._sharedShare = share;
      return popupRef.current;
    };

    const hidePopup = () => {
      if (popupRef.current) {
        try { popupRef.current.remove(); } catch { /* mapa destruido */ }
        popupRef.current = null;
      }
    };

    const showPopup = (lngLat, html, share) => {
      if (!lngLat || !html) {
        hidePopup();
        return;
      }
      const popup = ensurePopup(share);
      popup.setLngLat(lngLat).setHTML(html).addTo(map);
      if (share) bindShareTooltipBridge(popup);
    };

    const clearHoverVisual = () => {
      if (hoveredRef.current) {
        const prev = hoveredRef.current;
        hoveredRef.current = null;
        safeSetFeatureHoverState(prev, false);
      }
      hidePopup();
      canvas.style.cursor = '';
    };

    const clearHover = () => {
      clearHoverVisual();
      notifyHoverLot(null);
    };

    const invalidateLayerCache = () => { cachedLayersRef.current = []; };

    const isLayerQueryable = (layerId) => {
      if (!isMapAlive(map)) return false;
      try {
        if (!map.getLayer(layerId)) return false;
        return map.getLayoutProperty(layerId, 'visibility') !== 'none';
      } catch {
        return false;
      }
    };

    const getFeatureApproxArea = (feature) => {
      const geometry = feature?.geometry;
      if (!geometry || !Array.isArray(geometry.coordinates)) return Number.POSITIVE_INFINITY;

      let minLng = Number.POSITIVE_INFINITY;
      let minLat = Number.POSITIVE_INFINITY;
      let maxLng = Number.NEGATIVE_INFINITY;
      let maxLat = Number.NEGATIVE_INFINITY;
      let hasPoint = false;

      const consumePoint = (point) => {
        if (!Array.isArray(point) || point.length < 2) return;
        const lng = Number(point[0]);
        const lat = Number(point[1]);
        if (Number.isNaN(lng) || Number.isNaN(lat)) return;
        hasPoint = true;
        if (lng < minLng) minLng = lng;
        if (lng > maxLng) maxLng = lng;
        if (lat < minLat) minLat = lat;
        if (lat > maxLat) maxLat = lat;
      };

      if (geometry.type === 'Polygon') {
        geometry.coordinates.forEach((ring) => {
          if (!Array.isArray(ring)) return;
          ring.forEach(consumePoint);
        });
      } else if (geometry.type === 'MultiPolygon') {
        geometry.coordinates.forEach((polygon) => {
          if (!Array.isArray(polygon)) return;
          polygon.forEach((ring) => {
            if (!Array.isArray(ring)) return;
            ring.forEach(consumePoint);
          });
        });
      } else {
        return Number.POSITIVE_INFINITY;
      }

      if (!hasPoint) return Number.POSITIVE_INFINITY;
      return Math.abs((maxLng - minLng) * (maxLat - minLat));
    };

    const handleMouseMove = (event) => {
      if (rafIdRef.current !== null) cancelAnimationFrame(rafIdRef.current);

      rafIdRef.current = requestAnimationFrame(() => {
        rafIdRef.current = null;

        // Mapa destruido o sin estilo (cambio de estilo, `remove()`): nada que consultar.
        if (!enabledRef.current || !isMapAlive(map)) {
          clearHover();
          return;
        }

        const occludingIds = (occludingLayerIdsRef.current || [])
          .filter((layerId) => isLayerQueryable(layerId));
        if (occludingIds.length) {
          let occluded = false;
          try {
            occluded = map.queryRenderedFeatures(event.point, { layers: occludingIds }).length > 0;
          } catch {
            occluded = false;
          }
          if (occluded) {
            clearHover();
            return;
          }
        }

        if (map.getZoom() < MAP_MIN_LOT_ZOOM) {
          clearHover();
          return;
        }

        if (!cachedLayersRef.current.length) {
          cachedLayersRef.current = getInteractiveParcelLayerIds()
            .filter(isLayerQueryable);
        }
        if (!cachedLayersRef.current.length) { clearHover(); return; }

        let hit = [];
        try {
          hit = map.queryRenderedFeatures(
            event.point,
            { layers: cachedLayersRef.current },
          );
        } catch {
          hit = [];
        }

        if (!hit.length) { clearHover(); return; }

        // En superposición de geometrías (lote dentro de polígono mayor),
        // priorizamos la feature más específica (menor área aproximada) entre las
        // que admiten hover.
        const feature = hit
          .filter((candidate) => (
            candidate.id != null
            && (!canHoverRef.current || canHoverRef.current(candidate))
          ))
          .reduce((best, candidate) => {
            if (!best) return candidate;
            return getFeatureApproxArea(candidate) < getFeatureApproxArea(best) ? candidate : best;
          }, null);

        if (!feature) { clearHover(); return; }

        const { html: tooltipHtml, share } = resolveTooltip(feature);

        if (
          hoveredRef.current &&
          hoveredRef.current.id === feature.id &&
          hoveredRef.current.source === feature.source
        ) {
          // Reponemos el cursor en cada move: otros handlers de la vista (pins de
          // lista, hover administrativo) lo resetean al salir de sus capas y sin
          // esto quedaría en default sobre una parcela ya resaltada.
          canvas.style.cursor = HOVER_LOT_STYLE.cursor ?? 'pointer';
          if (tooltipHtml && event.lngLat) {
            showPopup(event.lngLat, tooltipHtml, share);
          } else {
            hidePopup();
          }
          return;
        }

        clearHoverVisual();

        hoveredRef.current = {
          source:      feature.source,
          sourceLayer: feature.sourceLayer,
          id:          feature.id,
        };

        if (safeSetFeatureHoverState(hoveredRef.current, true)) {
          canvas.style.cursor = HOVER_LOT_STYLE.cursor ?? 'pointer';
          notifyHoverLot(feature);
          if (tooltipHtml) showPopup(event.lngLat, tooltipHtml, share);
        } else {
          hoveredRef.current = null;
          canvas.style.cursor = '';
          notifyHoverLot(null);
        }
      });
    };

    const handleCanvasLeave = (event) => {
      if (isShareTooltip() && isNodeInPopup(event?.relatedTarget)) {
        return;
      }
      if (rafIdRef.current !== null) {
        cancelAnimationFrame(rafIdRef.current);
        rafIdRef.current = null;
      }
      clearHover();
    };

    const handleWindowBlur = () => clearHover();

    let cancelled = false;

    const setup = () => {
      if (cancelled) return;
      map.on('mousemove', handleMouseMove);
      map.on('styledata', invalidateLayerCache);
      canvas.addEventListener('mouseleave', handleCanvasLeave);
      window.addEventListener('blur',       handleWindowBlur);
    };

    const onLoad = () => setup();

    if (isMapAlive(map) && map.isStyleLoaded()) {
      setup();
    } else {
      map.once('load', onLoad);
    }

    return () => {
      cancelled = true;
      if (rafIdRef.current !== null) cancelAnimationFrame(rafIdRef.current);
      // Primero se sueltan los listeners: si `clearHover` fallara (mapa ya destruido por
      // `useMapInstance`, que limpia antes), el blur de ventana no queda colgado del mapa viejo.
      window.removeEventListener('blur',       handleWindowBlur);
      canvas.removeEventListener('mouseleave', handleCanvasLeave);
      map.off('load', onLoad);
      map.off('mousemove', handleMouseMove);
      map.off('styledata', invalidateLayerCache);
      try {
        clearHover();
      } catch {
        hoveredRef.current = null;
      }
    };
  }, [mapInstanceRef, getInteractiveParcelLayerIds]);

  useEffect(() => {
    const map = mapInstanceRef.current;
    if (!map || !enabled || !getInteractiveParcelLayerIds) return undefined;
    const lotId = externalHoverLotId != null && externalHoverLotId !== ''
      ? String(externalHoverLotId)
      : '';
    if (!lotId || !isMapAlive(map)) return undefined;

    const ids = [lotId];
    if (/^\d+$/.test(lotId)) ids.push(Number(lotId));
    const refs = [];
    getInteractiveParcelLayerIds().forEach((layerId) => {
      let layer = null;
      try { layer = map.getLayer(layerId); } catch { layer = null; }
      if (!layer) return;
      const sourceLayer = getLayerSourceLayer(layer);
      ids.forEach((id) => {
        const featureRef = {
          source: layer.source,
          id,
        };
        if (sourceLayer) featureRef.sourceLayer = sourceLayer;
        if (!canSetFeatureState(map, featureRef)) return;
        try {
          map.setFeatureState(featureRef, { hover: true });
          refs.push(featureRef);
        } catch {
          // feature todavía no está en el source
        }
      });
    });

    return () => {
      refs.forEach((featureRef) => {
        const mapHover = hoveredRef.current;
        if (
          mapHover
          && mapHover.source === featureRef.source
          && String(mapHover.id) === String(featureRef.id)
        ) {
          return;
        }
        try {
          if (canSetFeatureState(map, featureRef)) {
            map.setFeatureState(featureRef, { hover: false });
          }
        } catch {
          // ignore
        }
      });
    };
  }, [enabled, externalHoverLotId, getInteractiveParcelLayerIds, mapInstanceRef]);

  // Tooltip de publicación cuando el hover nace en la card del detalle.
  useEffect(() => {
    const map = mapInstanceRef.current;
    const tip = externalHoverPopup;
    if (!map || !enabled || !tip?.html || !tip.lngLat) return undefined;
    const popup = new maplibregl.Popup({
      closeButton: false,
      closeOnClick: false,
      focusAfterOpen: false,
      anchor: 'bottom',
      offset: 16,
      className: 'terrio-parcel-tooltip terrio-cad-share-tooltip',
      maxWidth: '260px',
    })
      .setLngLat(tip.lngLat)
      .setHTML(tip.html)
      .addTo(map);
    const el = popup.getElement?.();
    if (el) el.style.zIndex = '8';
    return () => popup.remove();
  }, [enabled, externalHoverPopup, mapInstanceRef]);
}
